import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.jsx'
import ChatBar, { GROUP_ORDER, relativeTime, lastPreview, groupByDate, displayTitle } from './ChatBar.jsx'
import PlotlyChart from './PlotlyChart.jsx'
import styles from './AnalyticsPage.module.css'

const ANALYTICS_PROMPTS = [
  'What are the key trends in my data this week?',
  'Show me revenue broken down by category',
  'Which metrics have changed the most recently?',
  'Compare this month vs last month',
  'What should I be paying attention to right now?',
]

function chartTitle(spec) {
  const t = spec?.layout?.title
  if (!t) return 'Chart'
  return (typeof t === 'string' ? t : t.text) || 'Chart'
}

export default function AnalyticsPage() {
  const { user, apiFetch, selectedTenantId } = useAuth()
  const { helpPrompt, consumeHelpPrompt } = useOutletContext()
  const chatRef = useRef(null)

  const [conversations, setConversations] = useState([])
  const [teamShared, setTeamShared] = useState([])
  const [activeConvId, setActiveConvId] = useState(null)
  const [streamingChart, setStreamingChart] = useState(null)
  const [studioTab, setStudioTab] = useState('visuals')
  const [pinning, setPinning] = useState({})   // { [chartId]: true }
  const [pinned, setPinned]   = useState({})   // { [chartId]: true }
  const [sections, setSections] = useState([{ id: 'overview', name: 'Overview', order: 0 }])
  const [pinMenu, setPinMenu] = useState(null) // chartId of open pin menu
  const [metrics, setMetrics] = useState([])
  const [recommendations, setRecommendations] = useState([])
  const [reviewJob, setReviewJob] = useState(null) // { jobId, status } | null
  const pinMenuRef = useRef(null)

  const handleConvsChange = useCallback((convs) => setConversations(convs), [])
  const handleTeamSharedChange = useCallback((convs) => setTeamShared(convs), [])
  const handleActiveConvChange = useCallback((id) => setActiveConvId(id), [])
  const handleChartGenerated = useCallback(({ spec }) => setStreamingChart(spec), [])

  useEffect(() => { setStreamingChart(null) }, [activeConvId])

  useEffect(() => {
    if (!helpPrompt) return
    chatRef.current?.setInput(helpPrompt)
    consumeHelpPrompt()
  }, [helpPrompt, consumeHelpPrompt])

  useEffect(() => {
    apiFetch('/api/dashboard/sections')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.sections) setSections(d.sections) })
      .catch(() => {})
  }, [apiFetch])

  useEffect(() => {
    apiFetch('/api/lake/metrics')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.metrics) setMetrics(d.metrics) })
      .catch(() => {})
  }, [apiFetch])

  const fetchRecommendations = useCallback(() => {
    apiFetch('/api/lake/recommendations')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.recommendations) setRecommendations(d.recommendations) })
      .catch(() => {})
  }, [apiFetch])

  useEffect(() => { fetchRecommendations() }, [fetchRecommendations])

  useEffect(() => {
    if (!pinMenu) return
    const handler = (e) => { if (!pinMenuRef.current?.contains(e.target)) setPinMenu(null) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [pinMenu])

  const handleSelectConv = useCallback((id) => chatRef.current?.openConversation(id), [])
  const handleNewConv    = useCallback(()    => chatRef.current?.startNewConversation(), [])
  const handleSelectShared = useCallback((conv) => chatRef.current?.openSharedConversation(conv), [])

  const sessionCharts = useMemo(() => {
    const allCharts = conversations.flatMap(conv =>
      (conv.messages ?? [])
        .filter(m => m.chartSpec)
        .map(m => ({ spec: m.chartSpec, id: m.id, sourceModels: m.sourceModels }))
    )
    if (streamingChart && !allCharts.some(c => c.spec === streamingChart)) {
      return [...allCharts, { spec: streamingChart, id: 'streaming', sourceModels: null }]
    }
    return allCharts
  }, [conversations, streamingChart])

  const handlePin = useCallback(async (chart, sectionId) => {
    if (pinning[chart.id] || pinned[chart.id]) return
    setPinMenu(null)
    setPinning(p => ({ ...p, [chart.id]: true }))
    try {
      await apiFetch('/api/dashboard/pin', {
        method: 'POST',
        body: JSON.stringify({
          title: chartTitle(chart.spec),
          chart_spec: chart.spec,
          source_models: chart.sourceModels ?? [],
          section_id: sectionId,
        }),
      })
      setPinned(p => ({ ...p, [chart.id]: true }))
    } finally {
      setPinning(p => ({ ...p, [chart.id]: false }))
    }
  }, [apiFetch, pinning, pinned])

  const pollReviewJob = useCallback((jobId) => {
    const timer = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/definition-jobs/${jobId}`)
        if (!res.ok) { clearInterval(timer); setReviewJob(null); return }
        const job = await res.json()
        if (job.status === 'success' || job.status === 'failed') {
          clearInterval(timer)
          if (job.status === 'success') fetchRecommendations()
          setTimeout(() => setReviewJob(null), 4000)
          setReviewJob({ jobId, status: job.status })
        } else {
          setReviewJob({ jobId, status: job.status })
        }
      } catch { clearInterval(timer); setReviewJob(null) }
    }, 3000)
  }, [apiFetch, fetchRecommendations])

  const runReview = useCallback(async () => {
    setReviewJob({ jobId: null, status: 'queued' })
    try {
      const res = await apiFetch('/api/lake/recommendations/review', { method: 'POST' })
      if (!res.ok) { setReviewJob(null); return }
      const { job_id } = await res.json()
      if (!job_id) { setReviewJob(null); return }
      setReviewJob({ jobId: job_id, status: 'running' })
      pollReviewJob(job_id)
    } catch {
      setReviewJob(null)
    }
  }, [apiFetch, pollReviewJob])

  const dismissRecommendation = useCallback(async (id) => {
    setRecommendations(prev => prev.filter(r => r.id !== id))
    try {
      await apiFetch(`/api/lake/recommendations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'dismissed' }),
      })
    } catch (err) {
      console.error('Dismiss recommendation error:', err)
    }
  }, [apiFetch])

  const lastReviewed = useMemo(() => {
    if (recommendations.length === 0) return null
    return recommendations.reduce((max, r) => (r.created_at > max ? r.created_at : max), recommendations[0].created_at)
  }, [recommendations])

  const groups = groupByDate(conversations)

  return (
    <div className={styles.root}>

      {/* Left: Conversation browser */}
      <aside className={styles.leftPanel}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Conversations</span>
          <button className={styles.newConvBtn} onClick={handleNewConv}>+ New</button>
        </div>
        <div className={styles.convList}>
          {conversations.length === 0 ? (
            <div className={styles.emptyConvs}>
              <div className={styles.emptyConvsIcon}>◈</div>
              <div className={styles.emptyConvsText}>No conversations yet</div>
              <button className={styles.emptyConvsStart} onClick={handleNewConv}>Start your first →</button>
            </div>
          ) : (
            GROUP_ORDER.filter(g => groups[g]).map(label => (
              <div key={label} className={styles.convGroup}>
                <div className={styles.convGroupLabel}>{label}</div>
                {groups[label].map(conv => (
                  <button
                    key={conv.id}
                    className={`${styles.convItem} ${conv.id === activeConvId ? styles.convActive : ''}`}
                    onClick={() => handleSelectConv(conv.id)}
                  >
                    <div className={styles.convItemRow}>
                      <span className={styles.convTitle}>{displayTitle(conv.title)}</span>
                      <span className={styles.convTime}>{relativeTime(conv.createdAt)}</span>
                    </div>
                    {conv.messages && (
                      <div className={styles.convPreview}>{lastPreview(conv.messages)}</div>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Center: Chat */}
      <div className={styles.chatPanel}>
        <ChatBar
          ref={chatRef}
          key={`${user?.sub}-${selectedTenantId || 'own'}`}
          userId={user?.sub}
          panelMode
          pageContext="analytics"
          suggestedPrompts={ANALYTICS_PROMPTS}
          onConversationsChange={handleConvsChange}
          onActiveConvChange={handleActiveConvChange}
          onChartGenerated={handleChartGenerated}
          onTeamSharedChange={handleTeamSharedChange}
        />
      </div>

      {/* Right: Studio */}
      <aside className={styles.rightPanel}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>Studio</span>
        </div>

        <div className={styles.studioTabs}>
          {['visuals', 'kpis', 'recommendations', 'shared'].map(tab => (
            <button
              key={tab}
              className={`${styles.studioTab} ${studioTab === tab ? styles.studioTabActive : ''}`}
              onClick={() => setStudioTab(tab)}
            >
              {tab === 'visuals' ? 'Visuals' : tab === 'kpis' ? 'KPIs' : tab === 'recommendations' ? 'Recommendations' : 'Shared'}
            </button>
          ))}
        </div>

        <div className={styles.studioBody}>

          {studioTab === 'visuals' && (
            sessionCharts.length === 0 ? (
              <div className={styles.emptyStudio}>
                <div className={styles.emptyStudioIcon}>▣</div>
                <div className={styles.emptyStudioText}>
                  Ask the AI to chart your data — visuals will appear here ready to pin.
                </div>
              </div>
            ) : (
              sessionCharts.map(chart => (
                <div key={chart.id} className={styles.assetCard}>
                  <div className={styles.assetChartWrap}>
                    <PlotlyChart spec={chart.spec} />
                  </div>
                  <div className={styles.assetCardFooter}>
                    <span className={styles.assetCardTitle}>{chartTitle(chart.spec)}</span>
                    {chart.id !== 'streaming' && (
                      pinned[chart.id] ? (
                        <span className={`${styles.pinBtn} ${styles.pinBtnDone}`}>✓ Pinned</span>
                      ) : pinning[chart.id] ? (
                        <span className={styles.pinBtn}>…</span>
                      ) : (
                        <div className={styles.pinWrap} ref={pinMenu === chart.id ? pinMenuRef : null}>
                          <button
                            className={styles.pinBtn}
                            onClick={() => setPinMenu(id => id === chart.id ? null : chart.id)}
                          >
                            ↑ Pin to Dashboard
                          </button>
                          {pinMenu === chart.id && (
                            <div className={styles.pinMenuDropdown}>
                              {sections.map(s => (
                                <button
                                  key={s.id}
                                  className={styles.pinMenuItem}
                                  onClick={() => handlePin(chart, s.id)}
                                >
                                  {s.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </div>
              ))
            )
          )}

          {studioTab === 'kpis' && (
            metrics.length === 0 ? (
              <div className={styles.emptyStudio}>
                <div className={styles.emptyStudioIcon}>◈</div>
                <div className={styles.emptyStudioText}>
                  Ask the AI to define a KPI — it will appear here once saved.
                </div>
              </div>
            ) : (
              metrics.map(m => {
                const formattedValue = m.current_value != null
                  ? (m.unit === 'USD' || m.unit === '$')
                      ? `$${Number(m.current_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : m.unit === '%'
                        ? `${Number(m.current_value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
                        : `${Number(m.current_value).toLocaleString()}${m.unit ? ` ${m.unit}` : ''}`
                  : '—'
                return (
                  <div key={m.name} className={styles.kpiCard}>
                    <div className={styles.kpiLabel}>{m.label}</div>
                    <div className={styles.kpiValue}>{formattedValue}</div>
                    <div className={styles.kpiMeta}>
                      <span className={styles.kpiModel}>{m.model}</span>
                      {m.value_computed_at && <span className={styles.kpiDate}>as of {m.value_computed_at}</span>}
                    </div>
                    <button
                      className={styles.kpiCalcBtn}
                      onClick={() => chatRef.current?.setInput(`Calculate the current value for the "${m.label}" KPI`)}
                    >
                      {m.current_value != null ? '↻ Recalculate' : 'Calculate →'}
                    </button>
                  </div>
                )
              })
            )
          )}

          {studioTab === 'recommendations' && (
            <>
              <div className={styles.recommendationsHeader}>
                <span className={styles.recommendationsLastReviewed}>
                  Last reviewed: {lastReviewed ? relativeTime(lastReviewed) : 'Never'}
                </span>
                <button
                  className={styles.kpiCalcBtn}
                  disabled={!!reviewJob && reviewJob.status !== 'success' && reviewJob.status !== 'failed'}
                  onClick={runReview}
                >
                  {reviewJob && reviewJob.status !== 'success' && reviewJob.status !== 'failed'
                    ? '↻ Reviewing…'
                    : '↻ Run review'}
                </button>
                {reviewJob?.status === 'success' && <span className={styles.docBadgeDone}>Done</span>}
                {reviewJob?.status === 'failed' && <span className={styles.docBadgeFail}>Failed</span>}
              </div>

              {recommendations.length === 0 ? (
                <div className={styles.emptyStudio}>
                  <div className={styles.emptyStudioIcon}>◎</div>
                  <div className={styles.emptyStudioText}>
                    No recommendations yet — run a review to get started.
                  </div>
                </div>
              ) : (
                recommendations.map(rec => (
                  <div key={rec.id} className={styles.recCard}>
                    <div className={styles.recCardHeader}>
                      <span className={`${styles.recPriority} ${styles[`recPriority_${rec.priority}`] || ''}`}>
                        {rec.priority}
                      </span>
                      <span className={styles.recCategory}>{rec.category?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className={styles.recTitle}>{rec.title}</div>
                    {rec.rationale && <div className={styles.recRationale}>{rec.rationale}</div>}
                    <div className={styles.recActions}>
                      <button
                        className={styles.kpiCalcBtn}
                        onClick={() => chatRef.current?.setInput(rec.suggested_prompt)}
                      >
                        Dig in →
                      </button>
                      <button
                        className={styles.recDismissBtn}
                        onClick={() => dismissRecommendation(rec.id)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {studioTab === 'shared' && (
            teamShared.length === 0 ? (
              <div className={styles.emptyStudio}>
                <div className={styles.emptyStudioIcon}>⤴</div>
                <div className={styles.emptyStudioText}>
                  Conversations your teammates share will appear here.
                </div>
              </div>
            ) : (
              teamShared.map(conv => (
                <button
                  key={conv.id}
                  className={styles.sharedCard}
                  onClick={() => handleSelectShared(conv)}
                >
                  <div className={styles.sharedCardRow}>
                    <span className={styles.sharedCardTitle}>{displayTitle(conv.title)}</span>
                    <span className={styles.convTime}>{relativeTime(conv.createdAt)}</span>
                  </div>
                  <div className={styles.convPreview}>{lastPreview(conv.messages)}</div>
                </button>
              ))
            )
          )}

        </div>
      </aside>

    </div>
  )
}
